import React, { FC, JSX, useEffect, useRef, useState } from 'react';
import Keys from '@/Translations/generated/da/product.json.keys';
import HeroKeys from '@/Translations/generated/da/hero.json.keys';
import TravelInformationKeys from '@/Translations/generated/da/TravelInformation.json.keys';
import ExtensionTeaserKeys from '@/Translations/generated/da/extensionTeaser.json.keys';
import PriceInfoKeys from '@/Translations/generated/da/priceInformation.json.keys';
import GeneralForHoldKeys from '@/Translations/generated/da/generalForHold.json.keys';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { facArrowDown } from '@/Theme/SVG/Icons';
import { useTranslation } from '@/app/i18n/client';
import { createPdf } from '@/components/itinerary/createPdf';
import type { ApiProduct } from '../../../api/model';
import { type PartialPageProps } from '@/types/cms/magnolia';
import type { ApiProductsCacheResponse } from '@ibe/api';
import { useSearchParams } from 'next/navigation';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { PDPSpinner } from '@/Theme/SVG/Svgs';
import { useTransitionState } from 'react-transition-state';
import classNames from 'classnames';
import { useClickAway } from 'react-use';
import Image from '@/components/image/Image';

const triggerPdfDownload = (url: string, pdfName: string): void => {
  const downloadLink = document.createElement('a');
  downloadLink.download = pdfName;
  downloadLink.href = url;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

const TextContainer: FC<{ isPdfLoading: boolean; pdfName: string; headline: string }> = ({
  isPdfLoading,
  pdfName,
  headline
}): JSX.Element => {
  const { t } = useTranslation('product');

  return (
    <>
      <div className="itinerary__pdf__popup__container">
        <div className="itinerary__pdf__popup__headline">
          {isPdfLoading ? headline : t(Keys.complete)}
        </div>
        {isPdfLoading ? (
          <PDPSpinner />
        ) : (
          <div className="itinerary__pdf__check">
            <FontAwesomeIcon icon={faCheck} />
          </div>
        )}
      </div>
      <div className="itinerary__pdf__popup__filename">
        {isPdfLoading ? `${pdfName}.pdf` : t(Keys.checkDownloadsFolder)}
      </div>
    </>
  );
};

const Pdf: FC<{
  pageProps?: PartialPageProps;
  product?: Partial<ApiProduct>;
  selectedPackage?: ApiProductsCacheResponse;
}> = ({ pageProps, product, selectedPackage }): JSX.Element => {
  const { t } = useTranslation('product');
  const { t: tHero } = useTranslation('hero');
  const { t: tPriceInfo } = useTranslation('priceInformation');
  const { t: tTravelInfo } = useTranslation('TravelInformation');
  const { t: tExtension } = useTranslation('extensionTeaser');
  const { t: tGeneralForHold } = useTranslation('generalForHold');
  const searchParams = useSearchParams();
  const { locale, fetchTTL } = pageProps ?? {};
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const loadPdfInitially = searchParams.get('pdf') === 'true';
  const pdfName = `${(product?.productTitle ?? 'product_pdf').replaceAll(' ', '_')}_${product?.name ?? ''}`;

  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [{ status, isMounted }, toggle] = useTransitionState({
    timeout: 300,
    mountOnEnter: true,
    unmountOnExit: true,
    preEnter: true
  });

  useClickAway(popupRef, () => {
    if (!isPdfLoading) {
      toggle(false);
    }
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (isMounted && popupRef.current && buttonRef.current) {
      timer = setTimeout(() => {
        if (popupRef.current && buttonRef.current) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const popupHeight = popupRef.current.offsetHeight ?? 100;
          const spaceBelow = window.innerHeight - buttonRect.bottom;
          const spaceAbove = buttonRect.top;
          if (spaceBelow < popupHeight && spaceAbove > popupHeight) {
            popupRef.current.classList.add('itinerary__pdf__popup--above');
          } else {
            popupRef.current.classList.remove('itinerary__pdf__popup--above');
          }
        }
      }, 10);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isMounted]);

  useEffect(() => {
    (async () => {
      if (loadPdfInitially) {
        await requestPdf(true);
      }
    })();
  }, [searchParams]);

  const requestPdf = async (initialLoading: boolean): Promise<void> => {
    if (!pdfUrl) {
      setIsPdfLoading(true);
      if (initialLoading) {
        setModalOpen(true);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 1000);
      } else {
        toggle(true);
      }
      const buffer = await createPdf({
        translations: {
          productAirportLabel: tHero(HeroKeys.departure),
          productDateLabel: tHero(HeroKeys.date),
          productDurationLabel: tHero(HeroKeys.duration),
          productLanguageLabel: tHero(HeroKeys.language),
          productPriceLabel: tHero(HeroKeys.price),
          days: tHero(HeroKeys.days),
          singleDay: t(Keys.singleDay),
          dailyProgram: t(Keys.dailyProgram),
          highlights: t(Keys.highlights),
          sections: {
            introduction: t(Keys.introduction),
            priceInformation: t(Keys.priceInformation),
            itinerary: t(Keys.dailyProgram)
          },
          priceInformation: {
            tripPrice: tPriceInfo(PriceInfoKeys.tripPrice),
            singleFee: tPriceInfo(PriceInfoKeys.singleFee),
            childPrice: tPriceInfo(PriceInfoKeys.childPrice),
            roomUpgrade: tPriceInfo(PriceInfoKeys.roomUpgrade),
            priceInfoExcursions: tPriceInfo(PriceInfoKeys.priceInfoExcursions),
            priceIncludes: tPriceInfo(PriceInfoKeys.priceIncludes),
            priceDoesNotInclude: tPriceInfo(PriceInfoKeys.priceDoesNotInclude),
            date: tPriceInfo(PriceInfoKeys.date),
            price: tPriceInfo(PriceInfoKeys.price)
          },
          travelInformation: {
            headline: tTravelInfo(TravelInformationKeys.header),
            type: tTravelInfo(TravelInformationKeys.type),
            language: tTravelInfo(TravelInformationKeys.language),
            visum: tTravelInfo(TravelInformationKeys.visum),
            vaccination: tTravelInfo(TravelInformationKeys.vaccination),
            currency: tTravelInfo(TravelInformationKeys.currency),
            yes: tTravelInfo(TravelInformationKeys.yes),
            no: tTravelInfo(TravelInformationKeys.no)
          },
          generalForHold: {
            generalForHoldHeadline: tGeneralForHold(GeneralForHoldKeys.generalForHoldHeadline),
            sectionHeader1: tGeneralForHold(GeneralForHoldKeys.sectionHeader1),
            sectionBody1: tGeneralForHold(GeneralForHoldKeys.sectionBody1),
            sectionHeader2: tGeneralForHold(GeneralForHoldKeys.sectionHeader2),
            sectionBody2: tGeneralForHold(GeneralForHoldKeys.sectionBody2),
            sectionHeader3: tGeneralForHold(GeneralForHoldKeys.sectionHeader3),
            sectionBody3: tGeneralForHold(GeneralForHoldKeys.sectionBody3),
            sectionHeader4: tGeneralForHold(GeneralForHoldKeys.sectionHeader4),
            sectionBody4: tGeneralForHold(GeneralForHoldKeys.sectionBody4),
            sectionHeader5: tGeneralForHold(GeneralForHoldKeys.sectionHeader5),
            sectionBody5: tGeneralForHold(GeneralForHoldKeys.sectionBody5),
            sectionHeader6: tGeneralForHold(GeneralForHoldKeys.sectionHeader6),
            sectionBody6: tGeneralForHold(GeneralForHoldKeys.sectionBody6),
            sectionHeader7: tGeneralForHold(GeneralForHoldKeys.sectionHeader7),
            sectionBody7: tGeneralForHold(GeneralForHoldKeys.sectionBody7),
            sectionHeader8: tGeneralForHold(GeneralForHoldKeys.sectionHeader8),
            sectionBody8: tGeneralForHold(GeneralForHoldKeys.sectionBody8),
            sectionHeader9: tGeneralForHold(GeneralForHoldKeys.sectionHeader9),
            sectionBody9: tGeneralForHold(GeneralForHoldKeys.sectionBody9),
            sectionHeader10: tGeneralForHold(GeneralForHoldKeys.sectionHeader10),
            sectionBody10: tGeneralForHold(GeneralForHoldKeys.sectionBody10),
            sectionHeader11: tGeneralForHold(GeneralForHoldKeys.sectionHeader11),
            sectionBody11: tGeneralForHold(GeneralForHoldKeys.sectionBody11),
            sectionHeader12: tGeneralForHold(GeneralForHoldKeys.sectionHeader12),
            sectionBody12: tGeneralForHold(GeneralForHoldKeys.sectionBody12),
            sectionHeader13: tGeneralForHold(GeneralForHoldKeys.sectionHeader13),
            sectionBody13: tGeneralForHold(GeneralForHoldKeys.sectionBody13)
          },
          extension: {
            title: tExtension(ExtensionTeaserKeys.title),
            duration: tExtension(ExtensionTeaserKeys.duration),
            perPerson: tExtension(ExtensionTeaserKeys.perPerson)
          },
          trips: t(Keys.trips)
        },
        locale: locale ?? '',
        product,
        pacificProduct: selectedPackage,
        fetchTTL
      });

      const blob = new Blob([new Uint8Array(buffer)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      triggerPdfDownload(url, pdfName);
      setIsPdfLoading(false);
    } else {
      triggerPdfDownload(pdfUrl, pdfName);
    }
  };

  return (
    <div className="itinerary__pdf">
      <button
        ref={buttonRef}
        className="btn btn-primary itinerary__btn"
        onClick={() => requestPdf(false)}
        aria-label="print page"
      >
        <div>
          <span className="itinerary__btn__text">
            {isPdfLoading ? t(Keys.pdfIsBeingCreated) : t(Keys.downloadFullProgram)}
          </span>
          {isPdfLoading ? (
            <Spinner className="itinerary__btn__spinner"></Spinner>
          ) : (
            <FontAwesomeIcon icon={facArrowDown} />
          )}
        </div>
      </button>
      {isMounted && (
        <div
          ref={popupRef}
          className={classNames('itinerary__pdf__popup', {
            'itinerary__pdf__popup--hidden': status === 'preEnter' || status === 'exiting'
          })}
        >
          {!isPdfLoading && (
            <FontAwesomeIcon
              className="itinerary__pdf__popup__close"
              icon={faTimes}
              onClick={() => toggle(false)}
            />
          )}
          <TextContainer
            isPdfLoading={isPdfLoading}
            pdfName={pdfName}
            headline={t(Keys.downloadingPDF)}
          />
        </div>
      )}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(value => !value)}
        className="itinerary__pdf__modal"
      >
        <ModalBody className="itinerary__pdf__modal__inner">
          <FontAwesomeIcon
            className="itinerary__pdf__popup__close"
            icon={faTimes}
            onClick={() => setModalOpen(false)}
          />
          <div className="itinerary__pdf__popup__image">
            <Image src="/logos/pdf.jpg" alt="pdf logo" width={100} height={100} noLoader />
          </div>
          <TextContainer
            isPdfLoading={isPdfLoading}
            pdfName={pdfName}
            headline={t(Keys.downloadingFullDoc)}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default Pdf;
